import { Typography } from "@/frontend/shared/ui/Typography/Typography";
import styles from "./DashboardHeader.module.scss";
import clsx from "clsx";
import { AddCompanies } from "@/frontend/features/Company/ui/AddCompanies/AddCompanies";
import { EContent } from "../types/types";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { TCompanyInfo, TPipeline } from "@/frontend/pages/DashboardPage/model/types/types";
import { NormalButton, SquareButton } from "@/frontend/shared/ui/Button";
import {
  CaretDownIcon,
  CaretLeftIcon,
  DotsThreeVerticalIcon,
  FilterIcon,
  PlusIcon,
  RenameIcon,
  TrashIcon
} from "@/frontend/shared/assets/icons";
import { AddStageModal } from "../../AddStageModal";
import { getRouteArchivedCompanies } from "@/frontend/shared/const/router";
import { ArchiveIcon } from "@/frontend/shared/assets/icons/ArchiveIcon";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { Badge } from "@/frontend/shared/ui/Badge/Badge";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { AddPipelineModal } from "../../AddPipelineModal";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { deletePipeline } from "@/frontend/pages/DashboardPage/model/services/deletePipeline";
import { AxiosError } from "axios";
import { RenameModal } from "../../RenameModal/RenameModal";
import { patchRenamePipeline } from "@/frontend/pages/DashboardPage/model/services/patchRenamePipeline";

type TDashboardHeaderProps = {
  className?: string;
  onChangeContent: Dispatch<SetStateAction<EContent>>;
  content: EContent;
  triggerRefetch: () => void;
  triggerRefetchAll: () => void;
  setBulkDataInfo: Dispatch<SetStateAction<TCompanyInfo[]>>;
  setFileUploadProgresses: Dispatch<SetStateAction<Record<string, number>>>;
  stageOptions: { label: string; value: string }[];
  numFilters: number;
  currentPipeline: TPipeline | null;
  setCurrentPipeline: (pipeline: TPipeline | null) => void;
  pipelinesData?: TPipeline[];
  isLoadingPipelines: boolean;
  refetchPipelines: () => void;
  setFiltersOpen: Dispatch<SetStateAction<boolean>>;
  filtersOpen: boolean;
};

export function DashboardHeader({
  className,
  onChangeContent,
  content,
  triggerRefetch,
  triggerRefetchAll,
  setBulkDataInfo,
  setFileUploadProgresses,
  stageOptions,
  numFilters,
  currentPipeline,
  setCurrentPipeline,
  pipelinesData,
  isLoadingPipelines,
  refetchPipelines,
  setFiltersOpen,
  filtersOpen
}: TDashboardHeaderProps) {
  const [isPipelineDropdownOpen, setIsPipelineDropdownOpen] = useState(false);
  const [isAddStageModalOpen, setIsAddStageModalOpen] = useState(false);
  const [isAddPipelineModalOpen, setIsAddPipelineModalOpen] = useState(false);
  const [isDeletePipelineModalOpen, setIsDeletePipelineModalOpen] = useState<null | number>(null);
  const [isRenamePipelineModalOpen, setIsRenamePipelineModalOpen] = useState<null | TPipeline>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [secondaryPipelineDropdownOpened, setSecondaryPipelineDropdownOpened] = useState<null | number>(null);
  const [pipelineName, setPipelineName] = useState("");

  const filterMenuButtonRef = useRef<HTMLButtonElement>(null);

  const { navigate } = useHistoricalNavigate();

  function onUploadCompany() {
    triggerRefetch();

    if (content === EContent.PORTFOLIO) {
      onChangeContent(EContent.PIPELINE);
    }
  }

  const isMobile = useScreenWidth() <= 768;

  async function onDeletePipeline(id: number) {
    try {
      setIsDeleting(true);
      await deletePipeline({ id });
      refetchPipelines();
      snackbarConstructor("Successfully deleted pipeline", "normal");
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      reportErrorToSentry(axiosError, "DASHBOARD: DELETE /pipelines/${id}");
      snackbarConstructor("Failed to delete pipeline", "error");
    } finally {
      setIsDeleting(false);
      setIsDeletePipelineModalOpen(null);
    }
  }

  async function onRenamePipeline(id: number) {
    try {
      await patchRenamePipeline({ id, name: pipelineName });
      refetchPipelines();
      snackbarConstructor("Successfully renamed pipeline", "normal");
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      console.error(error);
      reportErrorToSentry(axiosError, "DASHBOARD: rename /pipelines/${id}");
      snackbarConstructor("Failed to rename pipeline", "error");
    } finally {
      setIsRenamePipelineModalOpen(null);
      setPipelineName("");
    }
  }

  const pipelineDropdownOptions = (
    pipelinesData?.map((pipeline) => (
      <div className={styles.option} key={pipeline.id}>
        <NormalButton
          onClick={() => {
            setCurrentPipeline(pipeline);
            setIsPipelineDropdownOpen(false);
          }}
          variant="tertiary"
          size="large"
        >
          {pipeline.name}
        </NormalButton>
        <SquareButton
          size="large"
          variant="tertiary"
          onMouseDown={(e) => {
            e.stopPropagation();
            setSecondaryPipelineDropdownOpened(secondaryPipelineDropdownOpened !== pipeline.id ? pipeline.id : null);
          }}
        >
          <DotsThreeVerticalIcon />
        </SquareButton>
        <DropshadowMenu
          isOpen={secondaryPipelineDropdownOpened === pipeline.id}
          onClose={() => setSecondaryPipelineDropdownOpened(null)}
          options={[
            <NormalButton
              variant="tertiary"
              size="large"
              className={styles.pipelineButton}
              onClick={() => {
                setIsRenamePipelineModalOpen(pipeline);
                setPipelineName(pipeline.name);
              }}
            >
              <RenameIcon />
              Rename
            </NormalButton>,

            <NormalButton
              variant="tertiary"
              size="large"
              className={clsx(styles.deletePipelineBtn, !pipeline.canBeDeleted && styles.disabled)}
              onClick={() => setIsDeletePipelineModalOpen(pipeline.id)}
              disabled={!pipeline.canBeDeleted}
              color={!pipeline.canBeDeleted ? "greyscale400" : "greyscale900"}
            >
              <TrashIcon />
              <div>
                Delete Pipeline
                {!pipeline.canBeDeleted && (
                  <Typography variant="body-3" color="greyscale400">
                    Pipeline must be empty to delete
                  </Typography>
                )}
              </div>
            </NormalButton>
          ]}
          left={isMobile ? "0" : "calc(100% + 20px)"}
          top={isMobile ? "100%" : "0"}
          dividerIndexRate={1}
        />
      </div>
    )) || []
  ).concat([
    <NormalButton
      size="large"
      variant="tertiary"
      className={styles.addPipelineBtn}
      onClick={() => setIsAddPipelineModalOpen(true)}
    >
      <PlusIcon />
      Add Pipeline
    </NormalButton>
  ]);

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.leftSideContainer}>
        {content === EContent.PIPELINE && (
          <NormalButton
            onClick={() => setFiltersOpen((prev) => !prev)}
            size={"large"}
            className={styles.filterBtn}
            variant="secondary"
            id="pipelinePage-filterIcon"
            ref={filterMenuButtonRef}
          >
            {filtersOpen ? <CaretLeftIcon /> : <FilterIcon />}
            {!!numFilters && (
              <Badge bgColor="var(--innovera-orange500)" className={styles.badge}>
                {numFilters}
              </Badge>
            )}
            <Typography variant="label-4" color="greyscale900">
              Filters
            </Typography>
          </NormalButton>
        )}
        <div id="pipelinePage-pipelineDropdown">
          <NormalButton
            variant="secondary"
            size="large"
            className={styles.pipelineButton}
            isSubmitting={isLoadingPipelines}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsPipelineDropdownOpen((prev) => !prev);
            }}
          >
            {currentPipeline?.name}
            <CaretDownIcon />
          </NormalButton>
          <DropshadowMenu
            isOpen={isPipelineDropdownOpen}
            onClose={() => {
              setIsPipelineDropdownOpen(false);
              setSecondaryPipelineDropdownOpened(null);
            }}
            options={pipelineDropdownOptions}
            dividerIndexRate={pipelinesData?.length}
            className={styles.pipelineDropdown}
          />
        </div>

        {!isMobile && (
          <NormalButton
            variant="tertiary"
            size="large"
            className={styles.addStageButton}
            color="greyscale700"
            onClick={() => setIsAddStageModalOpen(true)}
            id="pipelinePage-addStageButton"
          >
            <PlusIcon className={styles.addStageIcon} />
            Add Stage
          </NormalButton>
        )}
      </div>
      <div className={clsx(styles.rightSideContainer, content === EContent.PIPELINE && styles.hasFilter)}>
        {!isMobile && (
          <SquareButton
            size="large"
            variant="secondary"
            onClick={() => navigate(getRouteArchivedCompanies())}
            id="pipelinePage-archiveButton"
          >
            <ArchiveIcon />
          </SquareButton>
        )}

        <AddCompanies
          triggerRefetch={onUploadCompany}
          triggerRefetchAll={triggerRefetchAll}
          setBulkDataInfo={setBulkDataInfo}
          setFileUploadProgresses={setFileUploadProgresses}
          setDashboardContent={onChangeContent}
          stageOptions={stageOptions}
          buttonId="pipelinePage-addCompaniesOrangeButton"
        />
        <AddStageModal
          isOpen={isAddStageModalOpen}
          onClose={() => setIsAddStageModalOpen(false)}
          stages={stageOptions}
          triggerRefetch={triggerRefetchAll}
        />
        <AddPipelineModal
          isOpen={isAddPipelineModalOpen}
          onClose={() => setIsAddPipelineModalOpen(false)}
          pipelines={pipelinesData || []}
          triggerRefetch={refetchPipelines}
          setCurrentPipeline={setCurrentPipeline}
        />
        <ConfirmationModal
          isOpen={!!isDeletePipelineModalOpen}
          onClose={() => setIsDeletePipelineModalOpen(null)}
          onConfirm={() => {
            onDeletePipeline(isDeletePipelineModalOpen as number);
          }}
          title="Delete Pipeline?"
          content="Are you sure you want to delete this pipeline? This action cannot be undone."
          confirmButtonText="Delete"
          dangerConfirm
          isDisabled={isDeleting}
        />
        <RenameModal
          isOpen={!!isRenamePipelineModalOpen}
          onClose={() => {
            setIsRenamePipelineModalOpen(null);
          }}
          onSuccess={() => {
            onRenamePipeline(isRenamePipelineModalOpen?.id as number);
          }}
          setValue={setPipelineName}
          value={pipelineName}
          title="Rename Pipeline"
          placeholder="Enter pipeline name"
          lengthLimit={30}
          restrictedOptions={pipelinesData
            ?.map((pipeline) => pipeline.name)
            .filter((pipeline) => pipeline !== isRenamePipelineModalOpen?.name)}
          valueName="Pipeline"
        />
      </div>
    </div>
  );
}
